// Shared, provider-neutral contract for the Ads Manager read boundary.  This
// deliberately contains no database or provider implementation details.

export type AdsEntityLevel = "campaign" | "adset" | "ad";

export type AdsManagerCampaignScope = "all" | "lead_generation";
export const ADS_MANAGER_SORTS = ["default", "spend_desc", "spend_asc", "outcomes_desc", "outcomes_asc"] as const;
export type AdsManagerSort = typeof ADS_MANAGER_SORTS[number];
export function parseAdsManagerSort(value: unknown): AdsManagerSort {
  return typeof value === "string" && ADS_MANAGER_SORTS.includes(value as AdsManagerSort)
    ? value as AdsManagerSort : "default";
}
/** Unknown spend sorts last in either direction; known zero remains a real value. */
export function compareAdsManagerSpend(
  left: { id: string; spend: number | null | undefined },
  right: { id: string; spend: number | null | undefined },
  sort: AdsManagerSort,
): number {
  const a = left.spend != null && Number.isFinite(left.spend) ? left.spend : null;
  const b = right.spend != null && Number.isFinite(right.spend) ? right.spend : null;
  if (a === null || b === null) return (a === null ? 1 : 0) - (b === null ? 1 : 0) || left.id.localeCompare(right.id);
  return (sort === "spend_desc" ? b - a : a - b) || left.id.localeCompare(right.id);
}
/** Use the first reported result that exists, including a known zero. */
export function reportedOutcomeValue(metrics?: {
  purchases?: number | null; checkouts?: number | null; addToCarts?: number | null; leads?: number | null;
  purchaseResults?: number | null; checkoutResults?: number | null; addToCartResults?: number | null; leadResults?: number | null;
  reportedRegistration?: AdsReportedRegistration;
  selectedPurchase?: AdsSelectedPurchase;
} | null): number | null {
  for (const value of [
    metrics?.purchases ?? metrics?.purchaseResults ?? selectedPurchaseCount(metrics?.selectedPurchase),
    metrics?.checkouts ?? metrics?.checkoutResults,
    metrics?.addToCarts ?? metrics?.addToCartResults,
    metrics?.leads ?? metrics?.leadResults,
    metrics?.reportedRegistration?.count,
  ]) {
    if (value != null && Number.isFinite(value)) return value;
  }
  return null;
}
export function compareAdsManagerOutcomes(
  left: { id: string; metrics?: Parameters<typeof reportedOutcomeValue>[0] },
  right: { id: string; metrics?: Parameters<typeof reportedOutcomeValue>[0] },
  sort: AdsManagerSort,
): number {
  const a = reportedOutcomeValue(left.metrics);
  const b = reportedOutcomeValue(right.metrics);
  if (a === null || b === null) return (a === null ? 1 : 0) - (b === null ? 1 : 0) || left.id.localeCompare(right.id);
  return (sort === "outcomes_desc" ? b - a : a - b) || left.id.localeCompare(right.id);
}
export type AdsDirectStatus = "active" | "paused" | "archived" | "deleted" | "unknown";
export type AdsOutcomeKind = "lead" | "purchase";
export const ADS_PERFORMANCE_SCOPES = ["full_account", "our_managed", "client_managed", "shared", "persona_creative"] as const;
export type AdsPerformanceScope = typeof ADS_PERFORMANCE_SCOPES[number];
export const ADS_CREATIVE_SOURCES = ["persona", "client", "other", "unknown"] as const;
export type AdsCreativeSource = typeof ADS_CREATIVE_SOURCES[number];
export const ADS_MANAGEMENT_OWNERS = ["our_team", "client", "shared", "unassigned", "inherit"] as const;
export type AdsManagementOwner = typeof ADS_MANAGEMENT_OWNERS[number];
export const ADS_TARGET_METRICS = ["cost_per_lead", "cost_per_purchase", "roas", "ctr", "cpm"] as const;
export type AdsTargetMetric = typeof ADS_TARGET_METRICS[number];
export type AdsConversionEvent = "lead" | "purchase";
export const PERFORMANCE_BRIEF_TEMPLATES = ["ecommerce", "lead_gen"] as const;
export type PerformanceBriefTemplate = typeof PERFORMANCE_BRIEF_TEMPLATES[number];
export function resolvePerformanceBriefTemplate(value: unknown): PerformanceBriefTemplate {
  return value === "lead_gen" ? "lead_gen" : "ecommerce";
}
export function shouldVerifyPerformanceBriefAccount(
  wasEnabled: boolean,
  enabled: boolean,
  previousAccountId: string | null,
  selectedAccountId: string | null,
): boolean {
  return enabled && (
    !wasEnabled
    || previousAccountId?.replace(/^act_/i, "") !== selectedAccountId?.replace(/^act_/i, "")
  );
}

export interface WindsorConnectedAccountRow {
  accountId: string;
  archivedAt?: Date | string | null;
}

/** Resolve the one Windsor account selected in brand analytics settings.
 * A row for some other account never grants access, and an archived matching
 * row is not usable. */
export function resolveWindsorConnectedAccount<T extends WindsorConnectedAccountRow>(
  settings: unknown,
  accounts: readonly T[],
): { connectedAccountId: string | null; account: T | null; state: "connected" | "missing" | "unconfigured" } {
  const raw = (settings as any)?.creativePerformance?.accountId;
  const connectedAccountId = raw == null ? null : String(raw).trim().replace(/^act_/i, "");
  if (!connectedAccountId) return { connectedAccountId: null, account: null, state: "missing" };
  const account = accounts.find(row => !row.archivedAt && String(row.accountId).replace(/^act_/i, "") === connectedAccountId) ?? null;
  return { connectedAccountId, account, state: account ? "connected" : "unconfigured" };
}

/** Decide whether a verified account needs a new brand-scoped reporting row.
 * Never revive an archived row or borrow another brand's connection. */
export function briefAccountProvisioning(
  brandId: string,
  selectedId: string,
  rows: readonly (WindsorConnectedAccountRow & { brandId: string })[],
): "existing" | "create" | "archived" | "owned_elsewhere" {
  const matches = rows.filter(row => String(row.accountId).replace(/^act_/i, "") === selectedId);
  if (matches.some(row => row.brandId !== brandId)) return "owned_elsewhere";
  if (matches.some(row => row.archivedAt)) return "archived";
  return matches.length ? "existing" : "create";
}

/** A provider-wide Windsor listing is verification of connectivity, never a
 * brand grant. The grant is issued separately by the brand-scoped transaction. */
export function verifyBriefReportingAccount(
  selectedId: string,
  analyticsName: string | null,
  windsorRows: readonly { account_id?: unknown }[],
): { state: "verified"; name: string } | { state: "analytics_unverified" | "windsor_unverified" } {
  if (!analyticsName?.trim()) return { state: "analytics_unverified" };
  if (!windsorRows.some(row =>
    typeof row.account_id === "string" && row.account_id.replace(/^act_/i, "") === selectedId
  )) return { state: "windsor_unverified" };
  return { state: "verified", name: analyticsName.trim() };
}

/** Provider metadata is never represented by a bare nullable value. `known`
 * means the provider authoritatively supplied the value; consumers must not
 * infer a missing value from names, reporting results, or an objective. */
export interface AdsAuthoritativeMetadata<T> {
  value: T | null;
  known: boolean;
  source: "meta" | "other";
}

/** Fixed, provider-supported settings catalog. Unsupported events are never inferred. */
export const ADS_PERFORMANCE_CAPABILITIES = {
  metrics: [
    { key: "cost_per_lead", label: "Cost per lead", unit: "currency", direction: "lower_is_better", events: ["lead"] },
    { key: "cost_per_purchase", label: "Cost per purchase", unit: "currency", direction: "lower_is_better", events: ["purchase"] },
    { key: "roas", label: "Return on ad spend", unit: "ratio", direction: "higher_is_better", events: ["purchase"] },
    { key: "ctr", label: "Click-through rate", unit: "percent", direction: "higher_is_better", events: [] },
    { key: "cpm", label: "Cost per thousand impressions", unit: "currency", direction: "lower_is_better", events: [] },
  ],
  events: ["lead", "purchase"],
  scopes: ADS_PERFORMANCE_SCOPES,
  windows: [7, 14, 30],
} as const;

export interface AdsDateRange { since: string; until: string; }

/** Bounded snapshot status contract: never exception text or provider bodies. */
export const ADS_MANAGER_SNAPSHOT_FAILURE_CODES = [
  "snapshot_read_timeout", "snapshot_read_rate_limit", "snapshot_read_retryable_http",
  "snapshot_read_network", "snapshot_read_authentication", "snapshot_read_field_contract",
  "snapshot_read_validation", "snapshot_read_provider_rejection", "snapshot_execution_budget",
  "empty_replacement", "incomplete_provider_data", "malformed_hierarchy", "claim_fenced", "provider_freshness_regressed",
  "provider_refresh_failed", "database_refresh_failed",
  "performance_provenance_missing", "performance_grain_incompatible", "performance_scope_incompatible",
  "performance_identity_missing", "performance_timestamp_missing", "performance_requalification_required",
  "daily_metadata_missing", "snapshot_recovery_exhausted",
] as const;
export type AdsManagerSnapshotFailureCode = typeof ADS_MANAGER_SNAPSHOT_FAILURE_CODES[number];
/** Sanitized durable recovery evidence, independent of last-good report content. */
export type AdsManagerRecovery = {
  /** Automatic restart allowance survives resetting an individual attempt. */
  automaticRestarts?: number;
  cumulativePasses?: number;
  /** Present on scheduled durable recovery; finite interactive counters remain separate. */
  consecutiveFailures?: number;
  restartNotBefore?: string;
  automaticTimezone?: string;
  escalated?: boolean;
  passes: number;
  noProgressPasses: number;
  committedDays: number;
  exhausted?: boolean;
  nextEligibleAt?: string;
  stop?: {
    code: string;
    requestPhase?: string;
    reportingDay?: string;
    category?: string;
    underlyingCategory?: string;
    stopReason?: "permanent" | "exhausted" | "deadline";
    attempts?: number;
    elapsedMs?: number;
  };
};
export function adsManagerSnapshotFailureCode(value: unknown): AdsManagerSnapshotFailureCode | undefined {
  return typeof value === "string" && ADS_MANAGER_SNAPSHOT_FAILURE_CODES.includes(value as AdsManagerSnapshotFailureCode)
    ? value as AdsManagerSnapshotFailureCode : undefined;
}

/** Base values only. Ratios are always calculated after rows are summed. */
export interface AdsMetrics {
  spend: number;
  impressions: number;
  reach: number | null;
  clicks: number;
  leads: number | null;
  addToCarts: number | null;
  checkouts: number | null;
  purchases: number | null;
  purchaseRevenue: number | null;
}

export interface AdsDerivedMetrics extends AdsMetrics {
  selectedPurchase?: AdsSelectedPurchase;
  /** Row observations are independent of objective-scoped lead KPIs. */
  observedLead?: AdsObservedLead;
  /** Explicit provider result, independent of purchase and eligible-lead metrics. */
  reportedRegistration?: AdsReportedRegistration;
  ctr: number | null;
  cpm: number | null;
  cpc: number | null;
  costPerLead: number | null;
  costPerAddToCart: number | null;
  costPerCheckout: number | null;
  costPerPurchase: number | null;
  roas: number | null;
}

export interface AdsObservedLead {
  count: number | null;
  costPerLead: number | null;
  spend: number | null;
  eventName: string;
  definition: "generic" | "configured";
  coverage: { knownAds: number; totalAds: number; state: "complete" | "partial" | "unavailable" };
  /** Comparable account/range/currency/attribution/delivery and event basis. */
  reportingBasis: string | null;
  /** Positive-count sources must agree; zero observations do not select a source. */
  eventEvidenceBasis?: string | null;
}

export interface AdsReportedRegistration {
  count: number | null;
  knownAds: number;
  totalAds: number;
  indicator: "actions:offsite_conversion.fb_pixel_complete_registration";
}

/** Separate from full-action totals. Complete means the retained population,
 * not that collection is fresh or today's account-local day has finished. */
export interface AdsSelectedPurchase {
  version: 1;
  countingBasis: "selected_result";
  attributionWindow: "default";
  identity: string | null;
  count: number | null;
  spend: number | null;
  costPerPurchase: number | null;
  coverage: { knownAds: number; totalAds: number; state: "complete" | "partial" | "unavailable" };
  reportingBasis: string | null;
}

export function selectedPurchaseCount(value?: AdsSelectedPurchase): number | null {
  return value?.coverage.state === "complete" && value.count != null
    && Number.isFinite(value.count) && value.count >= 0 ? value.count : null;
}

/** Immutable facts: these are intentionally not collapsed into one "origin". */
export interface AdsOriginFacts {
  provider: "meta" | "other";
  createdInPersonaOS: boolean;
  generatedInPersonaOS: boolean;
  exactCreativeMatch: boolean;
  sourceLaunchId?: string | null;
  matchedCreativeId?: string | null;
}

export interface AdsAssociations {
  productId?: string | null;
  offerId?: string | null;
  serviceId?: string | null;
  promotionId?: string | null;
  tags?: string[];
}

export interface AdsEntity {
  id: string;
  /** Presentation evidence only; never replaces the account-scoped write preflight. */
  deliveryReadiness?: "recent_launch_only" | "provider_observed" | "historical_reporting";
  /** Presentation candidate only. Never proves ownership or permits a write. */
  recentLaunchStatusRecovery?: boolean;
  accountId: string;
  name: string;
  directStatus: AdsDirectStatus;
  origin: AdsOriginFacts;
  associations?: AdsAssociations;
  tags?: string[];
  updatedAt?: string | null;
  issues?: Array<{ code: string; message: string; severity: "warning" | "error" }>;
}

export const ADS_MANAGER_RECENT_LAUNCH_DELIVERY_REASON =
  "Shown from a recent launch record. Reporting has not returned usable current status for this campaign in the selected account, so pause and resume are unavailable here. Refresh status without changing delivery, or inspect this campaign in Meta. The reason for the missing status is unknown.";

export function adsManagerDeliveryChangeAvailable(entity: {
  deliveryReadiness?: AdsEntity["deliveryReadiness"];
}) {
  return entity.deliveryReadiness !== "recent_launch_only" && entity.deliveryReadiness !== "historical_reporting";
}

export interface AdsCampaign extends AdsEntity {
  level: "campaign";
  createdTime?: string | null;
  startTime?: string | null;
  rangeReasons?: Array<"activity" | "created" | "scheduled_start">;
  /** Provider roll-up, retained for inspection only; reporting is ad-grain. */
  metrics?: AdsMetrics;
  objective?: string | null;
  objectiveMetadata?: AdsAuthoritativeMetadata<string>;
  settings?: Record<string, unknown>;
}
export interface AdsAdSet extends AdsEntity {
  level: "adset";
  campaignId: string;
  /** Provider roll-up, retained for inspection only; reporting is ad-grain. */
  metrics?: AdsMetrics;
  optimizationGoalMetadata?: AdsAuthoritativeMetadata<string>;
  conversionEventMetadata?: AdsAuthoritativeMetadata<AdsConversionEvent>;
  settings?: Record<string, unknown>;
}
export interface AdsAd extends AdsEntity {
  /** Request-local, revalidated from raw retained evidence, never trusted on read. */
  selectedPurchase?: AdsSelectedPurchase;
  /** Exact-range raw population evidence; legacy selectedOutcome alone is not enough. */
  purchaseReportingBasis?: {
    version: 1; range: AdsDateRange; timezone: string; accountGeneration?: number;
    rows: Record<string, any>[];
  };
  /** Request-local only; never persisted into the provider snapshot. */
  observedLead?: AdsObservedLead;
  level: "ad";
  campaignId: string;
  adsetId: string;
  currency?: string | null;
  metrics: AdsMetrics;
  /** Detail/recommendation evidence only; never substitutes for full action metrics. */
  selectedOutcome?: import("./performance-selected-outcome").PerformanceSelectedOutcome | null;
  /** Certified daily table population and raw counting/delivery basis, not a summary.
   * Null rows mean this ad was absent from that complete day's insight population. */
  dailyReportingBasis?: {
    version: 1;
    range: AdsDateRange;
    timezone: string;
    accountGeneration?: number;
    days: Array<{ day: string; row: Record<string, any> | null }>;
  };
  /** Raw event facts retained for request-time custom lead resolution in saved reads. */
  leadEventFacts?: Array<{ date?: string; actions?: unknown; results?: unknown }>;
  /** Optional same-range saved evidence. Never substitutes for known revised daily actions. */
  exactRangeEventFacts?: Array<{ date?: string; actions?: unknown; results?: unknown }>;
  creative?: { imageUrl?: string | null; videoUrl?: string | null; body?: string | null; headline?: string | null; cta?: string | null; destinationUrl?: string | null };
}

export interface AdsHierarchy {
  campaigns: AdsCampaign[];
  adsets: AdsAdSet[];
  ads: AdsAd[];
}

export interface AdsPage<T> {
  data: T[];
  nextCursor: string | null;
  /** true means the provider may have omitted data; totals/ranking must disclose it. */
  partial: boolean;
  issues?: string[];
}

export interface AdsSyncContext {
  accountId: string;
  accountGeneration?: number;
  timezone: string | null;
  attributionWindow: string | null;
  requestedRange: AdsDateRange;
  lastSuccessfulSyncAt: string | null;
  partial: boolean;
  /** Persistence-only coverage markers and provider limitations for this read. */
  issues?: string[];
  rateLimited?: boolean;
  /** Windsor campaign status evidence, not independently verified Meta state. */
  campaignDelivery?: AdsCampaignDeliveryObservation;
  /** Performance provider freshness, distinct from collection/receipt time. */
  performanceFreshness?: AdsPerformanceFreshness;
  /** Durable admission marker for existing-data recovery, not successful freshness. */
  performanceRecoveryRequestedAt?: string;
  /** One bounded range-to-daily handoff admission; not evidence of freshness. */
  performanceGrainRecoveryRequestedAt?: string;
}

export type AdsPerformanceFreshness = {
  /** 0 means some reused daily records predate freshness-aware collection. */
  version: 0 | 1;
  refreshRequested: boolean;
  refreshWindow: "3d";
  refreshInterval: "1h";
  receivedAt: string;
  oldestDataFetchedAt: string | null;
  newestDataFetchedAt: string | null;
  rowCount: number;
  missingTimestampRows: number;
  /** Server-side replacement evidence keyed by [reporting day or null, ad ID]. */
  rowDataFetchedAt?: Record<string, string | null>;
  /** Server commit evidence, separate from the current response's raw clocks. */
  collectionEvidence?: {
    acceptedOlderSource: boolean;
    sourceHighWatermark: string | null;
    sourceWatermarkAdvanced: boolean;
  };
  grain?: "range" | "daily";
  /** Bound by the collector, never derived from a receipt clock. */
  scope?: {
    accountId: string; timezone: string | null; attributionWindow: string | null;
    range: AdsDateRange; definition: string;
  };
};

export type AdsCampaignDeliveryObservation = {
  accountId: string;
  receivedAt: string;
  refreshRequested: true;
  missingFreshnessCount: number;
  rows: Array<{
    campaign_id: string;
    campaign_status: string | null;
    campaign_effective_status: string | null;
    data_fetched_at: string | null;
    receivedAt: string;
    freshness: "timestamped" | "unknown" | "conflict";
  }>;
};

/** Provider-only payload stored before any request-specific local enrichment. */
export type AdsManagerSnapshotPayload = {
  /** Request-only subset coverage. Never persist as a complete selected report. */
  partialCoverage?: AdsManagerPartialCoverage;
  hierarchy: AdsHierarchy;
  sync: AdsSyncContext;
  issues: string[];
  /** Optional development-only daily-store provenance; not full readiness. */
  dailyCoverage?: AdsManagerDailyCoverage;
  rangeReach?: { account: number; campaigns: Record<string, number>; adsets: Record<string, number> };
  /** Daily reports do not establish unique people or current delivery state. */
  dailySource?: boolean;
  exactEnrichmentFetchedAt?: string;
};

export type AdsManagerPartialCoverage = {
  savedDays: number; totalDays: number; missingDays: string[];
};

export type AdsManagerDailyReport = {
  day: string; timezone: string; currency: string | null;
  rows: Record<string, any>[]; fetchedAt: string;
  /** Day-scoped historical entity metadata, committed atomically with facts. */
  hierarchy?: AdsHierarchy;
  metadataFetchedAt?: string;
  providerFreshness?: AdsPerformanceFreshness;
};
/** Internal storage only. An unfinished facts checkpoint is not report coverage. */
export type AdsManagerDailyStoredReport = AdsManagerDailyReport & {
  metadataCheckpointOnly?: true;
  metadataCheckpoint?: AdsManagerDailyReport;
};
export type AdsManagerDailyCoverage = {
  totalDays: number; savedDays: number; freshDays: number;
  /** Today is explicitly partial and never part of the completed-day target. */
  completedDays?: number; partialToday?: boolean;
  oldestFetchedAt: string | null;
  exactRange: "pending" | "available";
  totals: { spend: number; impressions: number; clicks: number } | null;
  currency?: string | null;
  dailyReuse?: "reused" | "exact-range-fallback";
};

/** Read-only facts, never a qualified range report or current write evidence. */
export type AdsManagerSavedDailyData = {
  accountGeneration?: number;
  brandId: string; accountId: string; range: AdsDateRange; timezone: string;
  days: Array<{
    day: string;
    availability: "saved" | "missing" | "incompatible";
    status: "completed" | "partial" | null;
    fresh: boolean | null;
    collectedAt: string | null;
    sourceOldestAt: string | null;
    sourceNewestAt: string | null;
    currency: string | null;
    metrics: { spend: number; impressions: number; clicks: number } | null;
  }>;
};

/** One date and one hierarchy grain; intentionally has no operational fields. */
export type AdsManagerSavedEntity = {
  id: string; name: string; level: AdsEntityLevel;
  campaignId: string; adsetId: string | null;
  metrics: { spend: number; impressions: number; clicks: number };
  outcome: import("./performance-selected-outcome").PerformanceSelectedOutcome;
};
export type AdsManagerSavedEntityPage = {
  brandId: string; accountId: string; accountGeneration: number; range: AdsDateRange;
  day: string; level: AdsEntityLevel; campaignId: string | null; adsetId: string | null;
  availability: "saved" | "missing" | "incompatible";
  rows: AdsManagerSavedEntity[]; total: number; offset: number; nextOffset: number | null;
};

export interface AdsManagerProvider {
  listCampaigns(input: { accountId: string; cursor?: string; range: AdsDateRange }): Promise<AdsPage<AdsCampaign>>;
  listAdSets(input: { accountId: string; cursor?: string; range: AdsDateRange }): Promise<AdsPage<AdsAdSet>>;
  listAds(input: { accountId: string; cursor?: string; range: AdsDateRange }): Promise<AdsPage<AdsAd>>;
  getSyncContext(input: { accountId: string; range: AdsDateRange }): Promise<Omit<AdsSyncContext, "accountId" | "requestedRange" | "partial">>;
}

export interface AdsFilters {
  search?: string;
  directStatus?: AdsDirectStatus[];
  effectiveStatus?: "active" | "paused";
  origin?: "personaos" | "external";
  association?: Partial<AdsAssociations>;
  tags?: string[];
}

/** Only authoritative Meta metadata can classify a campaign. */
export function isAdsManagerLeadCampaign(campaign?: Pick<AdsCampaign, "objectiveMetadata"> | null): boolean {
  const metadata = campaign?.objectiveMetadata;
  return metadata?.known === true && metadata.source === "meta"
    && (metadata.value === "OUTCOME_LEADS" || metadata.value === "LEAD_GENERATION");
}

export function parseAdsManagerCampaignScope(value: unknown): AdsManagerCampaignScope {
  return value === "lead_generation" ? value : "all";
}
